import * as _ from 'lodash';
import {ApolloClient} from '../../state/types';
import {useApolloClient, useDeepMemo} from '../../state/hooks';
import {useEffect, useMemo, useState, useCallback} from 'react';
import {
  ArtifactIdQuery,
  ArtifactIdDocument,
  ArtifactPathInfoQuery,
  ArtifactPathInfoDocument,
  ArtifactManifestFromIdDocument,
  ArtifactManifestFromIdQuery,
  ArtifactFilesDocument,
  ArtifactFilesQuery,
} from '../../generated/graphql';
import * as Path from '../../util/path';
import * as Requests from '../../util/requests';
import {backendHost} from '../../config';
import {b64ToHex, hexToId} from '../../util/digest';
import {splitOnce} from '../../util/string';
import * as Types from './types';

export interface ObjectId {
  entityName: string;
  projectName: string;
  artifactTypeName: string;
  artifactSequenceName: string;
  artifactCommitHash: string;
}

interface ArtifactFileId {
  artifactId: string;
  path: string;
}

export type FullFilePath = ObjectId & {
  path: string;
};

function objectIdEqual(p1: ObjectId, p2: ObjectId) {
  return (
    p1.entityName === p2.entityName &&
    p1.projectName === p2.projectName &&
    p1.artifactTypeName === p2.artifactTypeName &&
    p1.artifactSequenceName === p2.artifactSequenceName &&
    p1.artifactCommitHash === p2.artifactCommitHash
  );
}

interface ArtifactPathInfo {
  entityName: string;
  projectName: string;
  artifactTypeName: string;
  artifactSequenceName: string;
  artifactCommitHash: string;
  artifactVersionIndex: number;
}

type ArtifactFileIdWithPathInfo = ArtifactFileId & ArtifactPathInfo;

interface ReadyManifest {
  manifest: Manifest;
  layout: 'V1' | 'V2';
  rootMetadata: DirMetadata;
}

export interface File {
  fullPath: FullFilePath | null;
  loading: boolean;
  contents: string | null;
}

export interface FileDirectUrl {
  fullPath: FullFilePath;
  refPath: ObjectId | null;
  loading: boolean;
  directUrl: string | null;
}
export interface DirMetadata {
  type: 'dir';
  fullPath: string;
  size: number;

  dirs: {[name: string]: DirMetadata};
  files: {[name: string]: FileMetadata};
}

export type FileMetadata = {
  type: 'file';
  fullPath: string;
  url: string;
} & FileEntry;
export type MetadataNode = DirMetadata | FileMetadata;

export interface FilePathWithMetadata {
  path: FullFilePath;
  metadata: MetadataNode | null;
}

export interface PathMetadata {
  fullPath: FullFilePath;
  loading: boolean;
  node: MetadataNode | null;
}

export interface LoadedPathMetadata {
  _type: 'loaded-path';
  fullPath: FullFilePath;
  node: MetadataNode | null;
}

export const isLoadedPathMetadata = (o: any): o is LoadedPathMetadata => {
  return o._type === 'loaded-path';
};

export interface FilePathMetadata {
  fullPath: FullFilePath;
  loading: boolean;
  node: FileMetadata;
}

interface FileEntry {
  size: number;
  ref?: string;
  digest?: string;
  birthArtifactID?: string;
}

interface Manifest {
  storagePolicy: string;
  storagePolicyConfig: {[key: string]: any};
  contents: {[name: string]: FileEntry};
}

// Global caches! Currently never cleaned up!

const artifactManifestCache: {
  [artifactId: string]: Promise<ReadyManifest>;
} = {};

const artifactFileCache: {
  [artifactId: string]: {
    [assetPath: string]: Promise<{
      refFileId: ArtifactFileIdWithPathInfo | null;
      contents: string | null;
    }>;
  };
} = {};

function artifactFileUrl(
  storagePolicy: string,
  storagePolicyConfig: {
    storageRegion?: string;
    storageLayout?: string;
  },
  defaultCloudRegion: string,
  entityName: string,
  entry: FileEntry
) {
  if (storagePolicy !== 'wandb-storage-policy-v1') {
    console.warn('unhandled storage policy');
    // Return a string for URL in this case. Clicking a download
    // link will be a no-op.
    return '';
  }
  const bucketRegion = storagePolicyConfig.storageRegion || defaultCloudRegion;
  const storageLayout = storagePolicyConfig.storageLayout || 'V1';

  if (entry.digest == null) {
    throw new Error('invalid');
  }
  switch (storageLayout) {
    case 'V1':
      return `${backendHost()}/artifacts/${entityName}/${b64ToHex(
        entry.digest
      )}`;
    case 'V2':
      return `${backendHost()}/artifactsV2/${bucketRegion}/${entityName}/${encodeURI(
        entry.birthArtifactID!
      )}/${b64ToHex(entry.digest)}`;
    default:
      console.warn(`unhandled storage layout: ${storageLayout}`);
      return '';
  }
}

function makeFileTree(
  entityName: string,
  defaultCloudRegion: string,
  manifest: Manifest
): DirMetadata {
  const fileEntries = manifest.contents;
  const fileTree: DirMetadata = {
    type: 'dir',
    fullPath: '',
    size: 0,
    dirs: {},
    files: {},
  };
  _.map(fileEntries, (entry, name) => {
    let currentFolder = fileTree;
    // 'media/images/image01.jpg' => ['media','images','image01.jpg']
    const path = name.split('/');
    while (path.length > 1) {
      // The following is safe to do because we made sure path had elems in the loop condition.
      const folderName = path.shift() as string;
      // create subfolder if it doesn't already exist
      if (!currentFolder.dirs[folderName]) {
        currentFolder.dirs[folderName] = {
          type: 'dir',
          fullPath:
            currentFolder.fullPath === ''
              ? folderName
              : currentFolder.fullPath + '/' + folderName,
          size: 0,
          dirs: {},
          files: {},
        };
      }
      currentFolder.dirs[folderName].size += entry.size;
      currentFolder = currentFolder.dirs[folderName];
    }
    // if we've come to the last item in the path, add this file object to the current folder
    currentFolder.files[path[0]] = {
      type: 'file',
      fullPath: name,
      url: entry.ref
        ? entry.ref
        : artifactFileUrl(
            manifest.storagePolicy,
            manifest.storagePolicyConfig,
            defaultCloudRegion,
            entityName,
            entry
          ),
      ...entry,
    };
  });
  return fileTree;
}

export function lookupNode(
  dir: DirMetadata,
  path: string
): MetadataNode | null {
  if (path === '') {
    return dir;
  }
  const pathItems = path.split('/');
  for (const pathItem of pathItems.slice(0, pathItems.length - 1)) {
    dir = dir.dirs[pathItem];
    if (dir == null) {
      return null;
    }
  }
  const lastItem = pathItems[pathItems.length - 1];
  return dir.dirs[lastItem] || dir.files[lastItem] || null;
}

function artifactFileIdsEqual(id1: ArtifactFileId, id2: ArtifactFileId) {
  return id1.artifactId === id2.artifactId && id1.path === id2.path;
}

async function loadArtifactPathInfo(
  client: ApolloClient,
  artifactId: string
): Promise<ArtifactPathInfo> {
  const result = await client.query<ArtifactPathInfoQuery>({
    query: ArtifactPathInfoDocument,
    variables: {
      artifactId,
    },
  });
  const artifact = result.data.artifact;
  if (artifact == null) {
    throw new Error('invalid artifact id');
  }
  if (artifact.commitHash == null || artifact.versionIndex == null) {
    throw new Error('artifact missing commitHash or versionIndex');
  }
  return {
    entityName: artifact.artifactType.project.entityName,
    projectName: artifact.artifactType.project.name,
    artifactTypeName: artifact.artifactType.name,
    artifactSequenceName: artifact.artifactSequence.name,
    artifactCommitHash: artifact.commitHash,
    artifactVersionIndex: artifact.versionIndex,
  };
}

async function loadDirectUrl(
  client: ApolloClient,
  layout: string,
  artifactGqlID: string,
  assetPath: string
) {
  const result = await client.query<ArtifactFilesQuery>({
    query: ArtifactFilesDocument,
    fetchPolicy: 'no-cache',
    variables: {
      layout,
      artifactID: artifactGqlID,
      artifactFileNames: [assetPath],
    },
  });
  return result.data?.artifact?.files.edges[0].node?.directUrl ?? null;
}

async function loadArtifactManifest(
  client: ApolloClient,
  artifactId: string
): Promise<ReadyManifest> {
  const manifestResult = await client.query<ArtifactManifestFromIdQuery>({
    query: ArtifactManifestFromIdDocument,
    fetchPolicy: 'no-cache',
    variables: {
      artifactId,
    },
  });

  const artifact = manifestResult.data?.artifact;
  if (artifact == null) {
    throw new Error('artifact not found');
  }
  const defaultCloudRegion =
    manifestResult.data?.serverInfo?.defaultCloudRegion.id;
  if (defaultCloudRegion == null) {
    throw new Error('invalid state: server missing defaultCloudRegion');
  }

  const directUrl = artifact.currentManifest?.file.directUrl;
  if (directUrl == null) {
    throw new Error('invalid state: artifact missing manifest');
  }

  const manifestString = await Requests.getUrlWithRetry(directUrl);
  let manifest: Manifest;
  try {
    manifest = JSON.parse(manifestString);
  } catch {
    throw new Error("invalid state: couldn't parse manifest");
  }

  const layout = manifest.storagePolicyConfig.storageLayout ?? 'V1';

  return {
    manifest,
    layout,
    rootMetadata: makeFileTree(
      artifact.artifactType.project.entityName,
      defaultCloudRegion,
      manifest
    ),
  };
}

async function cachedLoadArtifactManifest(
  client: ApolloClient,
  artifactId: string
) {
  let cachedManifest = artifactManifestCache[artifactId];
  if (cachedManifest == null) {
    cachedManifest = loadArtifactManifest(client, artifactId);
    artifactManifestCache[artifactId] = cachedManifest;
  }
  return cachedManifest;
}

export async function loadArtifactFilePathMetadata(
  client: ApolloClient,
  artifactId: string,
  assetPath: string
) {
  const manifest = await cachedLoadArtifactManifest(client, artifactId);
  return lookupNode(manifest.rootMetadata, assetPath);
}

export function parseArtifactRef(ref: string) {
  const url = new URL(ref);
  if (url.protocol !== 'wandb-artifact:') {
    throw new Error("Can't load reference to: " + ref);
  }
  let refPath = url.pathname;
  if (!refPath.startsWith('//')) {
    throw new Error('Invalid artifact reference ' + ref);
  }
  refPath = refPath.slice(2);
  const [refArtifactIdHex, refAssetPath] = splitOnce(refPath, '/');
  if (refAssetPath == null) {
    throw new Error('Invalid artifact reference ' + ref);
  }
  const refArtifactId = hexToId(refArtifactIdHex);
  return {artifactId: refArtifactId, assetPath: refAssetPath};
}

// TODO: Make sure we don't have multiple of these in flight
export async function loadArtifactFileDirectUrl(
  client: ApolloClient,
  artifactId: string,
  assetPath: string
) {
  const manifest = await cachedLoadArtifactManifest(client, artifactId);
  const manifestEntry = manifest.manifest.contents[assetPath];
  if (manifestEntry == null) {
    return {
      refFileId: null,
      directUrl: null,
    };
  }
  if (manifestEntry.ref != null) {
    // Check for artifact references. If we find one load it, and return
    // the fileId along with the directUrl.
    const url = new URL(manifestEntry.ref);
    if (url.protocol === 'wandb-artifact:') {
      return loadArtifactReferenceDirectUrl(client, manifestEntry.ref);
    } else if (url.protocol.match(/https?:/)) {
      return {
        refFileId: null,
        directUrl: manifestEntry.ref,
      };
    } else if (url.protocol === 's3:') {
      return loadS3ReferenceDirectUrl(manifestEntry.ref);
    } else if (url.protocol === 'gs:') {
      return loadGSReferenceDirectUrl(manifestEntry.ref);
    } else {
      throw new Error("Can't load reference to: " + manifestEntry.ref);
    }
  } else {
    if (manifestEntry.digest == null) {
      throw new Error('artifact file missing digest');
    }
    const directUrl = await loadDirectUrl(
      client,
      manifest.layout,
      artifactId,
      assetPath
    );
    return {
      refFileId: null,
      directUrl,
    };
  }
}

async function loadArtifactReferenceDirectUrl(
  client: ApolloClient,
  ref: string
) {
  const {artifactId: refArtifactId, assetPath: refAssetPath} = parseArtifactRef(
    ref
  );
  const refManifest = await cachedLoadArtifactManifest(client, refArtifactId);

  const layout = refManifest.manifest.storagePolicyConfig.storageLayout ?? 'V1';
  const refManifestEntry = refManifest.manifest.contents[refAssetPath];
  if (refManifestEntry.digest == null) {
    throw new Error('invalid');
  }
  const pathInfo = await loadArtifactPathInfo(client, refArtifactId);
  const directUrl = await loadDirectUrl(
    client,
    layout,
    refArtifactId,
    refAssetPath
  );
  return {
    refFileId: {artifactId: refArtifactId, path: refAssetPath, ...pathInfo},
    directUrl,
  };
}

async function loadS3ReferenceDirectUrl(ref: string) {
  const url = new URL(ref);

  const path = url.pathname;

  const match = path.match(/^\/\/(.+?)\/(.+)$/);
  if (!match || match.length !== 3) {
    throw new Error('Invalid S3 reference ' + ref);
  }
  const [, bucketName, objectKey] = match;

  return {
    refFileId: null,

    // this scheme may not work for all buckets -- see https://docs.aws.amazon.com/AmazonS3/latest/dev/UsingBucket.html#access-bucket-intro
    // the documentation implies that the region name is mandatory (e.g. s3-us-west-2.amazonaws.com)
    // for "region-specific" buckets, but this was not my experience. Also, bucket names with periods

    // we can use the S3 SDK to get the region (or the object!) but this requires an
    // identity pool: https://docs.aws.amazon.com/sdk-for-javascript/v2/developer-guide/s3-example-photos-view.html#s3-example-photos-view-scenario-prerequisites
    directUrl: `https://${bucketName}.s3.amazonaws.com/${objectKey}`,
  };
}

async function loadGSReferenceDirectUrl(ref: string) {
  const url = new URL(ref);

  const path = url.pathname;

  const match = path.match(/^\/\/(.+?)\/(.+)$/);
  if (!match || match.length !== 3) {
    throw new Error('Invalid GS reference ' + ref);
  }
  const [, bucketName, objectKey] = match;

  return {
    refFileId: null,
    directUrl: `https://storage.googleapis.com/${bucketName}/${objectKey}`,
  };
}

async function loadArtifactFileContent(
  client: ApolloClient,
  artifactId: string,
  assetPath: string
) {
  const {directUrl, refFileId} = await loadArtifactFileDirectUrl(
    client,
    artifactId,
    assetPath
  );
  if (directUrl == null) {
    return {
      refFileId: null,
      contents: null,
    };
  }
  const fileContents = await Requests.getUrlWithRetry(directUrl);

  return {
    refFileId,
    contents: fileContents,
  };
}

// TODO: provide a mechanism for users to parse results and cache that (without caching
// the content string)
export async function cachedLoadArtifactFileContent(
  client: ApolloClient,
  artifactId: string,
  assetPath: string
) {
  let cachedFile = artifactFileCache[artifactId]?.[assetPath];
  if (cachedFile == null) {
    cachedFile = loadArtifactFileContent(client, artifactId, assetPath);
    if (artifactFileCache[artifactId] == null) {
      artifactFileCache[artifactId] = {};
    }
    artifactFileCache[artifactId][assetPath] = cachedFile;
  }
  return cachedFile;
}

function useParallelAsyncMap<T, R>(
  items: Array<T | null>,
  options: {skip?: boolean} | undefined,
  getResult: (item: T) => Promise<R>,
  itemsEqual: (i1: T, i2: T) => boolean
) {
  const nullableItemsEqual = useCallback(
    (i1: T | null, i2: T | null) => {
      if (i1 == null || i2 == null) {
        return i1 == null && i2 == null;
      }
      return itemsEqual(i1, i2);
    },
    [itemsEqual]
  );

  // DeepMemo this so the caller doesn't have to worry about ref-equality
  items = useDeepMemo(items);
  options = useDeepMemo(options);

  const [result, setResult] = useState<
    Array<{item: T | null; loading: boolean; result: R | null}>
  >([]);

  // Fetch new fileIds
  useEffect(() => {
    if (options?.skip) {
      return;
    }

    const updateIndexes: number[] = [];
    for (let i = 0; i < items.length; i++) {
      if (result[i] == null || !nullableItemsEqual(result[i].item, items[i])) {
        updateIndexes.push(i);
      }
    }
    if (updateIndexes.length === 0) {
      return;
    }

    const newResult: typeof result = [...result];
    // Always set result fileIds to the passed in fileIds, before
    // anything asynchronous happens. This guarantees that result
    // has the latest fileIds
    for (const updateIndex of updateIndexes) {
      newResult[updateIndex] = {
        item: items[updateIndex],
        loading: true,
        result: null,
      };
    }
    setResult(newResult);

    const updateItems = updateIndexes.map(i => items[i]);
    const proms = updateItems.map(item =>
      item != null ? getResult(item) : null
    );
    Promise.all(proms).then(itemResults => {
      setResult(latestResult => {
        const newPromiseResult: typeof result = [...latestResult];
        for (let i = 0; i < updateItems.length; i++) {
          const resultIndex = updateIndexes[i];
          const curResult = latestResult[resultIndex];
          const curItem = items[resultIndex];
          // Only update if fileId still matches our target result slot
          if (
            curResult == null ||
            nullableItemsEqual(curResult.item, curItem)
          ) {
            newPromiseResult[resultIndex] = {
              item: curItem,
              loading: false,
              result: itemResults[i],
            };
          }
        }
        return newPromiseResult;
      });
    });
  }, [items, result, getResult, nullableItemsEqual, options]);

  // Clean up glitches: ensure that output fileIds always exactly match
  // incoming fileIds
  const finalResult = useMemo(() => {
    const itemResults = items.map((item, i) => {
      if (result[i] == null || !nullableItemsEqual(result[i].item, item)) {
        return {
          item,
          loading: true,
          result: null,
        };
      } else {
        return result[i];
      }
    });
    return {
      loading: itemResults.some(r => r.loading),
      results: itemResults,
    };
  }, [items, result, nullableItemsEqual]);

  return finalResult;
}

export function useArtifactManifest(artifactId?: string) {
  const client = useApolloClient();
  const [manifest, setManifest] = useState<ReadyManifest | null>();
  const [loading, setLoading] = useState(false);

  const loadArtifactManifestAsync = useCallback(async () => {
    setLoading(true);
    if (artifactId) {
      const result = await cachedLoadArtifactManifest(client, artifactId);
      setManifest(result);
    }
    setLoading(false);
  }, [client, artifactId]);

  useEffect(() => {
    loadArtifactManifestAsync();
  }, [loadArtifactManifestAsync]);

  return {loading, manifest};
}

export function useArtifactFilePathMetadata(
  fileIds: Array<ArtifactFileId | null>,
  options?: {skip?: boolean}
) {
  const client = useApolloClient();
  const loadArtifactFilePathMetadataCallback = useCallback(
    (fileId: ArtifactFileId) =>
      loadArtifactFilePathMetadata(client, fileId.artifactId, fileId.path),
    [client]
  );
  return useParallelAsyncMap(
    fileIds,
    options,
    loadArtifactFilePathMetadataCallback,
    artifactFileIdsEqual
  );
}

export function useArtifactDirectUrls(
  fileIds: Array<ArtifactFileId | null>,
  options?: {skip?: boolean}
) {
  const client = useApolloClient();
  const loadArtifactFileDirectUrlCallback = useCallback(
    (fileId: ArtifactFileId) =>
      loadArtifactFileDirectUrl(client, fileId.artifactId, fileId.path),
    [client]
  );
  return useParallelAsyncMap(
    fileIds,
    options,
    loadArtifactFileDirectUrlCallback,
    artifactFileIdsEqual
  );
}

export function useArtifactFileContent(
  fileIds: Array<ArtifactFileId | null>,
  options?: {skip?: boolean}
) {
  const client = useApolloClient();
  const loadArtifactFileContentCallback = useCallback(
    (fileId: ArtifactFileId) =>
      cachedLoadArtifactFileContent(client, fileId.artifactId, fileId.path),
    [client]
  );
  return useParallelAsyncMap(
    fileIds,
    options,
    loadArtifactFileContentCallback,
    artifactFileIdsEqual
  );
}

export function useArtifactIds(
  filePaths: Array<ObjectId | null>,
  options?: {skip?: boolean}
) {
  const client = useApolloClient();
  const loadArtifactIdsCallback = useCallback(
    (filePath: ObjectId) =>
      client
        .query<ArtifactIdQuery>({
          query: ArtifactIdDocument,
          variables: {
            entityName: filePath.entityName,
            projectName: filePath.projectName,
            artifactTypeName: filePath.artifactTypeName,
            artifactID:
              filePath.artifactSequenceName + ':' + filePath.artifactCommitHash,
          },
        })
        .then(idResp => idResp.data.project?.artifactType?.artifact?.id),
    [client]
  );
  return useParallelAsyncMap(
    filePaths,
    options,
    loadArtifactIdsCallback,
    objectIdEqual
  );
}

///// The functions below preserve the current interface that loads files by
// fullPath. We will probably switch to loading by just id.

export type UsePathMetadata = (filePaths: FullFilePath[]) => PathMetadata[];

export const usePathMetadata: UsePathMetadata = filePaths => {
  filePaths = useDeepMemo(filePaths);

  const idsResult = useArtifactIds(filePaths);
  const result = useArtifactFilePathMetadata(
    idsResult.results.map((r, i) => {
      const filePath = filePaths[i];
      const artifactId = r.result;
      return filePath == null || artifactId == null
        ? null
        : {
            artifactId,
            path: filePath.path,
          };
    }),
    {skip: idsResult.loading}
  );

  const finalResult = useMemo(() => {
    return result.results.map((r, i) => ({
      fullPath: filePaths[i],
      loading: r.loading,
      node: r.result ?? null,
    }));
  }, [filePaths, result.results]);
  return finalResult;
};

export type UseFileDirectUrl = (filePaths: FullFilePath[]) => FileDirectUrl[];
export const useFileDirectUrl: UseFileDirectUrl = filePaths => {
  filePaths = useDeepMemo(filePaths);

  const idsResult = useArtifactIds(filePaths);
  const result = useArtifactDirectUrls(
    idsResult.results.map((r, i) => {
      const filePath = filePaths[i];
      const artifactId = r.result;
      return filePath == null || artifactId == null
        ? null
        : {
            artifactId,
            path: filePath.path,
          };
    }),
    {skip: idsResult.loading}
  );

  const finalResult = useMemo(() => {
    return result.results.map((r, i) => ({
      fullPath: filePaths[i],
      refPath: r.result?.refFileId ?? null,
      loading: r.loading,
      directUrl: r.result?.directUrl ?? null,
    }));
  }, [filePaths, result.results]);
  return finalResult;
};

export type UseFileContent = (
  filePaths: Array<FullFilePath | null>,
  options?: {skip?: boolean}
) => File[];

export const useFileContent: UseFileContent = (filePaths, options) => {
  filePaths = useDeepMemo(filePaths);
  options = useDeepMemo(options);

  const idsResult = useArtifactIds(filePaths, {skip: options?.skip});
  const result = useArtifactFileContent(
    idsResult.results.map((r, i) => {
      const filePath = filePaths[i];
      const artifactId = r.result;
      return filePath == null || artifactId == null
        ? null
        : {
            artifactId,
            path: filePath.path,
          };
    }),
    {skip: idsResult.loading || options?.skip}
  );

  const finalResult = useMemo(() => {
    return result.results.map((r, i) => ({
      fullPath: r.result?.refFileId ?? filePaths[i],
      loading: r.loading,
      contents: r.result?.contents ?? null,
    }));
  }, [filePaths, result.results]);
  return finalResult;
};

export const getPathType = (pm: LoadedPathMetadata): Types.Type => {
  if (pm.node == null) {
    return null;
  } else if (pm.node?.type === 'dir') {
    return {type: 'dir' as const};
  } else {
    const filePath = pm.fullPath.path;
    const result: Types.File = {
      type: 'file' as const,
      extension: Path.extension(filePath),
    };
    if (filePath.endsWith('.json')) {
      const components = filePath.split('.');
      if (components.length >= 3) {
        const potentialWbObjectType = components[components.length - 2];
        if (Types.isMediaType(potentialWbObjectType)) {
          result.wbObjectType = potentialWbObjectType;
        }
      }
    }
    return result;
  }
};

export const getPathsType = (paths: LoadedPathMetadata[]): Types.Type => {
  const pathTypes = paths.map(getPathType);
  if (pathTypes.length === 1) {
    return pathTypes[0];
  }
  const pathType0 = pathTypes[0];
  if (pathTypes.slice(1).some(pt => !_.isEqual(pt, pathType0))) {
    return {type: 'dict' as const, objectType: 'any' as const};
  }
  return {
    type: 'dict' as const,
    objectType: pathType0,
  };
};

export function isFullFilePath(
  queryPath: ObjectId | FullFilePath
): queryPath is FullFilePath {
  return (queryPath as any).path != null;
}

export function toString(queryPath: ObjectId | FullFilePath): string {
  return `/${queryPath.entityName}/${queryPath.projectName}/${
    queryPath.artifactTypeName
  }/${queryPath.artifactSequenceName}:${queryPath.artifactCommitHash.slice(
    0,
    6
  )}${isFullFilePath(queryPath) ? '/' + queryPath.path : ''}`;
}
